import { Puzzle } from 'lucide-react';
import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
  useSidebar,
} from '@/components/ui/sidebar';
import tinoLogoDark from '@/assets/brand/tino-logo-dark.png';
import tinoLogo from '@/assets/brand/tino-logo.png';
import tinoMark from '@/assets/brand/tino-mark.png';
import { useLanguage } from '../../i18n';
import { NavigationLink, useNavigationTarget } from '../../lib/navigationMemory';
import { useAccess } from '../../lib/access';
import { isAddonEntryOpen } from '../../nav/addonMenu';
import { findHub, hubUrl, locate } from '../../nav/hubs';
import { ENTRY_ICONS, MODULE_ICONS } from '../../nav/icons';
import { ENABLED_MODULES, findModule, moduleFor, moduleGroups, moduleHome, sidebarEntryFor, type ModuleId, type SidebarEntry } from '../../nav/modules';
import { useAddonNav } from './AddonNav';
import { useDossierReviewCount } from './DossierBadge';

type Lookup = (key: string) => string;

/**
 * The sidebar of the owner's service-console reference: a white rail (navy in dark mode) under
 * the Tino logo (its cloud mark when collapsed), the module picker
 * ("Không gian quản lý": Nền tảng & thương mại, Domain Registrar, Backorder, Đấu giá — only the
 * modules that exist, `nav/modules.ts`), the picked module's groups of flat entries and a
 * "Dùng chung" group; the entry of the current page lit with an accent bar, and the number of
 * dossiers waiting for review next to "Hồ sơ". Every hub tab is on its hub page. Under "Tiện
 * ích", the menu entries of the running add-ons the user may open (`nav/addonMenu.ts`), in every
 * module.
 */
export function AppSidebar() {
  const { t } = useTranslation();
  const { t: tu } = useTranslation('ux');
  const tr = tu as unknown as Lookup;
  const access = useAccess();
  const navigate = useNavigate();
  const target = useNavigationTarget();
  const { pathname, search } = useLocation();
  const { setOpenMobile } = useSidebar();
  const current = sidebarEntryFor(locate(pathname, search));
  const dossiers = useDossierReviewCount();
  const addonEntries = useAddonNav();
  const lang = useLanguage();
  const close = () => setOpenMobile(false);
  const [picked, setPicked] = useState<ModuleId | undefined>(undefined);
  // A module chosen in the picker wins while its landing entry is open (that entry may be shared).
  const [choice, setChoice] = useState<{ module: ModuleId; entry: string } | null>(null);
  const moduleId = choice && current?.id === choice.entry ? choice.module : moduleFor(current?.id, picked);
  // Remember the module shown, so a shared entry (orders, customers) keeps it (derived state, set while rendering).
  if (picked !== moduleId) setPicked(moduleId);

  /** Shown when the user may open that tab (or any tab of the hub). */
  const visible = (e: SidebarEntry) => {
    const hub = findHub(e.hub);
    const tabs = hub ? access.tabs(hub) : [];
    return e.tab ? tabs.some((x) => x.id === e.tab) : tabs.length > 0;
  };
  const modules = ENABLED_MODULES.filter((m) => m.groups.some((g) => g.entries.some(visible)));
  const module = findModule(moduleId) ?? modules[0];
  const pick = (id: string) => {
    const m = findModule(id);
    const home = m ? m.groups.flatMap((g) => g.entries).find(visible) ?? moduleHome(m) : undefined;
    if (!m || !home) return;
    setPicked(m.id);
    setChoice({ module: m.id, entry: home.id });
    navigate(target(hubUrl(home.hub, home.tab)));
  };
  const ModuleIcon = module ? MODULE_ICONS[module.id] : undefined;

  return (
    <Sidebar collapsible="icon" variant="sidebar">
      <SidebarHeader className="pt-4">
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton size="lg" asChild tooltip={t('app.name')} className="hover:bg-transparent">
              <Link to="/" onClick={close}>
                <img src={tinoMark} alt="Tino" className="hidden size-8 shrink-0 group-data-[collapsible=icon]:block" />
                <div className="grid flex-1 gap-1 text-left leading-tight group-data-[collapsible=icon]:hidden">
                  <img src={tinoLogo} alt="Tino" className="h-5 w-auto dark:hidden" />
                  <img src={tinoLogoDark} alt="Tino" className="hidden h-5 w-auto dark:block" />
                  <span className="truncate text-[9px] font-semibold tracking-[0.12em] text-sidebar-foreground/80 uppercase">{t('app.short')}</span>
                </div>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>
      {modules.length > 1 && module ? (
        <div className="mx-2 mb-1 rounded-lg border border-sidebar-border bg-sidebar-accent/40 p-2.5 group-data-[collapsible=icon]:hidden">
          <label htmlFor="module-picker" className="mb-1.5 block text-[9px] font-semibold tracking-[0.12em] text-sidebar-foreground/70 uppercase">
            {tr('modules.label')}
          </label>
          <div className="relative">
            {ModuleIcon ? <ModuleIcon className="pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-sidebar-foreground/80" aria-hidden="true" /> : null}
            <select
              id="module-picker"
              aria-label={tr('modules.pick')}
              className="h-9 w-full cursor-pointer appearance-none rounded-md border border-sidebar-border bg-sidebar pr-7 pl-8 text-[13px] font-semibold text-sidebar-foreground outline-none focus-visible:ring-2 focus-visible:ring-sidebar-ring"
              value={module.id}
              onChange={(e) => pick(e.target.value)}
            >
              {modules.map((m) => (
                <option key={m.id} value={m.id}>
                  {tr(`modules.${m.id}`)}
                </option>
              ))}
            </select>
            <span aria-hidden="true" className="pointer-events-none absolute top-1/2 right-2.5 -translate-y-1/2 text-[10px] text-sidebar-foreground/70">
              ▾
            </span>
          </div>
        </div>
      ) : null}
      <SidebarContent className="gap-0">
        {(module ? moduleGroups(module) : []).map((group) => {
          const entries = group.entries.filter(visible);
          if (!entries.length) return null;
          return (
            <SidebarGroup key={group.id} className="py-1">
              <SidebarGroupLabel className="text-[10px] font-semibold tracking-[0.1em] text-sidebar-foreground/70 uppercase">{tr(`sidebar.groups.${group.id}`)}</SidebarGroupLabel>
              <SidebarMenu>
                {entries.map((e) => {
                  const Icon = ENTRY_ICONS[e.id];
                  const label = tr(`sidebar.entries.${e.id}`);
                  const active = current?.id === e.id;
                  return (
                    <SidebarMenuItem key={e.id} className="relative">
                      {active ? <span aria-hidden="true" className="absolute top-2 bottom-2 -left-2 w-[3px] rounded-full bg-sidebar-ring group-data-[collapsible=icon]:hidden" /> : null}
                      <SidebarMenuButton asChild isActive={active} tooltip={label} className="h-9 text-[13px] text-sidebar-foreground data-[active=true]:font-semibold">
                        <NavigationLink to={hubUrl(e.hub, e.tab)} onClick={close} aria-current={active ? 'page' : undefined}>
                          {Icon ? <Icon /> : null}
                          <span>{label}</span>
                        </NavigationLink>
                      </SidebarMenuButton>
                      {e.id === 'dossiers' && dossiers > 0 ? (
                        <SidebarMenuBadge className="rounded bg-sidebar-accent px-1.5 text-[10px] text-sidebar-accent-foreground">{dossiers > 99 ? '99+' : dossiers}</SidebarMenuBadge>
                      ) : null}
                    </SidebarMenuItem>
                  );
                })}
              </SidebarMenu>
            </SidebarGroup>
          );
        })}
        {addonEntries.length ? (
          <SidebarGroup className="py-1" data-sidebar-addons>
            <SidebarGroupLabel className="text-[10px] font-semibold tracking-[0.1em] text-sidebar-foreground/70 uppercase">{tr('sidebar.groups.addons')}</SidebarGroupLabel>
            <SidebarMenu>
              {addonEntries.map((e) => {
                const label = lang === 'en' ? e.label.en : e.label.vi;
                const active = isAddonEntryOpen(e, pathname, search);
                return (
                  <SidebarMenuItem key={e.id} className="relative">
                    {active ? <span aria-hidden="true" className="absolute top-2 bottom-2 -left-2 w-[3px] rounded-full bg-sidebar-ring group-data-[collapsible=icon]:hidden" /> : null}
                    <SidebarMenuButton asChild isActive={active} tooltip={label} className="h-9 text-[13px] text-sidebar-foreground data-[active=true]:font-semibold">
                      <Link to={e.url} onClick={close} aria-current={active ? 'page' : undefined} data-addon-entry={e.id}>
                        <Puzzle />
                        <span>{label}</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                );
              })}
            </SidebarMenu>
          </SidebarGroup>
        ) : null}
      </SidebarContent>
      <SidebarRail />
    </Sidebar>
  );
}
